<script setup lang="ts">
// [ node_modules ]
import { load } from 'recaptcha-v3';
import dayjs from 'dayjs';
import * as $gtag from 'vue-gtag';

// [lib]
import { ModalControl, InitModalControl, InitModals } from '@/lib/lib-front-modal-control';
import { GaFunc } from '@/lib/lib-front';
// [ lib - api ]
import { LibApiResuestMessage } from '@/lib/lib-api-request-message';

// [stores]
import { useAppStore } from '@/stores/app-store';
import { useLangStore } from '@/stores/lang-store';
// [locales]
import { ja } from '@/locales/contact-us/lang-ja';
import { en } from '@/locales/contact-us/lang-en';
// -----------------------------------------------------
// [ store - lang ]
const langStore = useLangStore();
langStore.setMessage(en, ja);
const t = langStore.t;
// -----------------------------------------------------
// [ Nuxt ]
const config = useRuntimeConfig();
const router = useRouter();
const route = useRoute();
// [ stores ]
const appStore = useAppStore();
// -----------------------------------------------------
definePageMeta({
  middleware: 'redirect',
  layout: 'default',
});
useHead({
  meta: [{ name: 'keywords', content: () => t('seo.keywords') }],
});
// プロパティ詳細は以下URL参照すること
// https://github.com/harlan-zw/zhead/blob/main/src/metaFlat.ts
useSeoMeta({
  title: () => t('seo.title'),
  ogTitle: () => t('seo.title'),
  description: () => t('seo.description'),
  ogDescription: () => t('seo.description'),
  ogImage: config.public.url + 'assets/img/OGP.png',
  twitterCard: 'summary_large_image',
});
// -----------------------------------------------------
const fixedGroupStype = computed(() => {
  return {
    'min-height': appStore.windowFixedH,
    'min-width': appStore.windowFixedW,
  };
});

const state = appStore.state.contactForm;

watch(
  () => state.checked,
  (value) => {
    if (value) {
      state.checkTs = dayjs().format();
    } else {
      state.checkTs = '';
    }
  }
);

const checkEmail = computed(() => {
  const pattern =
    /^[A-Za-z0-9]{1}([A-Za-z0-9_.-]*|[A-Za-z0-9_.-]*[+]{0,1}[A-Za-z0-9_.-]+)@{1}[A-Za-z0-9_.-]+\.[A-Za-z0-9]+$/;
  if (!state.email) return false;
  if (pattern.test(state.email)) {
    /* パターンにマッチした場合 */
    return false;
    // return false;
  } else {
    return true;
    // return false
  }
});

const emailError = computed(() => (checkEmail.value ? t(`formLabel.emailError`) : ''));

const isInput = computed(() => {
  if (!state.name) return false;
  if (!state.email) return false;
  if (!state.message) return false;
  if (!state.checked) return false;
  if (!state.checkTs) return false;
  if (checkEmail.value) return false;
  return true;
});

const getToken = async () => {
  try {
    const recaptcha = await load(config.public.google.reCaptcha.siteKey);
    return await recaptcha.execute('request_message');
  } catch (error) {
    console.error(error);
    return null;
  }
};

const requestSend = async () => {
  const ts = dayjs().format('YYYY-MM-DD HH:mm:ss.SSS');
  const token = await getToken();
  if (!token) return;
  try {
    $gtag.event(GaFunc.contactSendBefore, { ts: ts });
    state.isLoadingSend = true;
    const payload: LibApiResuestMessage.Req = {
      name: state.name,
      company: state.company,
      email: state.email,
      tel: state.tel,
      message: state.message,
      checkTs: state.checkTs,
      recaptchaToken: token,
    };
    const { data } = await useFetch('/api/request-message', {
      method: 'post',
      server: false,
      body: payload,
    });
    if (!data.value) {
      modal.snackbar.state.color = 'deep-orange-darken-3';
      modal.snackbar.state.message = t('requestSend.connectionError');
      modal.snackbar.show();
      // console.log(data.value);
      return;
    }
    if (data.value.completed === true) {
      setTimeout(() => {
        modal.checkInput.close();
      }, 400);
      modal.snackbar.state.color = 'green-darken-2';
      modal.snackbar.state.message = t('requestSend.requestCompleted');
    } else {
      modal.snackbar.state.color = 'deep-orange-darken-3';
      modal.snackbar.state.message = t('requestSend.requestError');
      console.error(data.value.message);
    }
    modal.snackbar.show();
    $gtag.event(GaFunc.contactSendAfter, { ts: ts, result: 1 });
  } catch (error: any) {
    console.error(error);
    $gtag.event(GaFunc.contactSendAfter, { ts: ts, result: 0, message: error.message });
  } finally {
    state.isLoadingSend = false;
  }
};

// [ Modal ]
interface Modal {
  checkInput: ModalControl;
  snackbar: ModalControl<{
    message: string;
    color: string;
  }>;
}
const modal = reactive<Modal>({
  checkInput: InitModalControl(),
  snackbar: InitModalControl({
    state: {
      message: '',
      color: 'deep-orange-darken-3',
    },
  }),
});

const breadcrumbsList = [
  {
    title: 'Home',
    disabled: false,
    url: `/${langStore.state.lang}`,
  },
  {
    title: 'About us',
    disabled: false,
    url: `/${langStore.state.lang}/about-us`,
  },
  {
    title: 'Contact us',
    disabled: true,
    url: '',
  },
];
const checkInput = (key: string) => {
  if ((state as any)[key].replace(/[ \s\n\t]/g, '').length === 0) {
    (state as any)[key] = '';
  }
};
onMounted(() => {
  InitModals(modal, nextTick);
});
// ---------------------------------------

const clickBreadCrumbs = (url: string) => {
  $gtag.event(GaFunc.navigate, {
    from: route.path,
    to: url,
    name: 'clickBreadCrumbs',
  });
};
// ---------------------------------------
</script>
<template>
  <div class="">
    <div ref="" class="fixed-group bg-dark" :style="fixedGroupStype">
      <div class="breadcrumbs">
        <v-breadcrumbs :items="breadcrumbsList" class="breadcrumbs-list">
          <template #title="{ item, index }">
            <NuxtLink :to="item.url" class="breadcrumbs-item" @click="clickBreadCrumbs(item.url)">
              <v-icon v-if="index != 0" icon="mdi-chevron-right" class="sp-divider"></v-icon> {{ item.title }}
            </NuxtLink>
          </template>
          <template #divider>
            <v-icon icon="mdi-chevron-right" class="pc-divider"></v-icon>
          </template>
        </v-breadcrumbs>
      </div>
      <div class="card">
        <div class="card-header bg-blue-grey-darken-2">
          <div class="">{{ t('title') }}</div>
        </div>
        <div class="card-body">
          <v-form class="form">
            <v-row>
              <v-col cols="12" sm="12">
                <v-text-field
                  v-model="state.name"
                  :label="t(`formLabel.name`)"
                  :rules="[(value: any) => !!value || t('formLabel.requireMessage')]"
                  variant="outlined"
                  clearable
                  hide-details="auto"
                  @blur="checkInput('name')"
                />
              </v-col>
              <v-col cols="12" sm="12">
                <v-text-field
                  v-model="state.company"
                  :label="t(`formLabel.company`)"
                  variant="outlined"
                  clearable
                  hide-details="auto"
                  @blur="checkInput('company')"
                />
              </v-col>
              <v-col cols="12" sm="12">
                <v-text-field
                  v-model="state.email"
                  type="email"
                  :label="t(`formLabel.email`)"
                  variant="outlined"
                  clearable
                  hide-details="auto"
                  :rules="[(value: any) => !!value || t('formLabel.requireMessage')]"
                  :error="checkEmail"
                  :error-messages="emailError"
                  @blur="checkInput('email')"
                />
              </v-col>
              <v-col cols="12" sm="12">
                <v-text-field
                  v-model="state.tel"
                  type="tel"
                  :label="t(`formLabel.tel`)"
                  variant="outlined"
                  hint="For example, 090-0000-0000"
                  clearable
                  hide-details="auto"
                  @blur="checkInput('tel')"
                />
              </v-col>
              <v-col cols="12" sm="12">
                <v-textarea
                  v-model="state.message"
                  :label="t(`formLabel.message`)"
                  :rules="[(value: any) => !!value || t('formLabel.requireMessage')]"
                  variant="outlined"
                  clearable
                  hide-details="auto"
                  @blur="checkInput('message')"
                />
              </v-col>
            </v-row>
            <v-row justify="center">
              <v-col cols="12">
                <div class="note">
                  <div class="note-title">{{ t('PrivacyPolicy.title') }}</div>
                  <div class="note-body">
                    <div class="sub-title">
                      {{ t('PrivacyPolicy.subTitle') }}
                    </div>
                    <div class="index">{{ t('PrivacyPolicy.r1.title') }}</div>
                    <div class="text">{{ t('PrivacyPolicy.r1.text') }}</div>
                    <div class="index">{{ t('PrivacyPolicy.r2.title') }}</div>
                    <div class="text">{{ t('PrivacyPolicy.r2.text') }}</div>
                    <div class="index">{{ t('PrivacyPolicy.r3.title') }}</div>
                    <div class="text">{{ t('PrivacyPolicy.r3.text') }}</div>
                    <ul class="list">
                      <li class="list-item">{{ t('PrivacyPolicy.r3.list1') }}</li>
                      <li class="list-item">{{ t('PrivacyPolicy.r3.list2') }}</li>
                      <li class="list-item">{{ t('PrivacyPolicy.r3.list3') }}</li>
                    </ul>
                    <div class="index">{{ t('PrivacyPolicy.r4.title') }}</div>
                    <div class="text">{{ t('PrivacyPolicy.r4.text') }}</div>
                    <div class="index">{{ t('PrivacyPolicy.r5.title') }}</div>
                    <div class="text">{{ t('PrivacyPolicy.r5.text') }}</div>
                    <div class="index">{{ t('PrivacyPolicy.r6.title') }}</div>
                    <div class="text">{{ t('PrivacyPolicy.r6.text') }}</div>
                    <div class="index">{{ t('PrivacyPolicy.r7.title') }}</div>
                    <div class="text">{{ t('PrivacyPolicy.r7.text') }}</div>
                    <div class="info">
                      <div class="">{{ t('PrivacyPolicy.info.name') }}</div>
                      <div class="">{{ t('PrivacyPolicy.info.address') }}</div>
                      <div class="">
                        {{ t('PrivacyPolicy.info.mail') }}<a href="mailto:sales@inspire-llc.jp">sales@inspire-llc.jp</a>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="state.checked"
                  color="light-blue-darken-3"
                  :label="t(`formLabel.check`)"
                ></v-checkbox>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  variant="flat"
                  size="x-large"
                  color="light-blue-darken-3"
                  class="ms-auto me-auto"
                  :disabled="!isInput"
                  @click="modal.checkInput.show()"
                >
                  {{ t(`formLabel.checkBtn`) }}
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </div>
      </div>
    </div>
    <ClientOnly>
      <Teleport to="body">
        <div class="modal-container" :class="{ isShow: modal.checkInput.isShow }">
          <div class="card check-card" @click.stop>
            <div class="card-header bg-blue-grey-darken-2 text-white">{{ t(`checkModal.title`) }}</div>
            <div class="card-body">
              <div class="check-card-title mb-4">{{ t(`checkModal.note`) }}</div>
              <v-text-field
                v-model="state.name"
                :label="t(`formLabel.name`)"
                variant="outlined"
                hide-details="auto"
                class="mb-4"
                readonly
              />
              <v-text-field
                v-model="state.company"
                :label="t(`formLabel.company`)"
                variant="outlined"
                hide-details="auto"
                class="mb-4"
                readonly
              />
              <v-text-field
                v-model="state.email"
                :label="t(`formLabel.email`)"
                variant="outlined"
                hide-details="auto"
                class="mb-4"
                readonly
              />
              <v-text-field
                v-model="state.tel"
                class="mb-4"
                :label="t(`formLabel.tel`)"
                variant="outlined"
                hide-details="auto"
                readonly
              />
              <v-textarea
                v-model="state.message"
                class="mb-4"
                :label="t(`formLabel.message`)"
                variant="outlined"
                :hide-details="true"
                readonly
              />
              <div class="d-flex">
                <v-btn
                  variant="flat"
                  size="x-large"
                  color="blue-grey-lighten-2"
                  class="me-auto"
                  @click="modal.checkInput.close()"
                >
                  {{ t(`com.back`) }}
                </v-btn>
                <v-btn
                  variant="flat"
                  size="x-large"
                  color="light-blue-darken-3"
                  class="ms-auto"
                  :loading="state.isLoadingSend"
                  @click="requestSend()"
                >
                  {{ t(`checkModal.send`) }}
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </Teleport>
      <v-snackbar v-model="modal.snackbar.isShow" :color="modal.snackbar.state.color">
        {{ modal.snackbar.state.message }}
        <template #actions>
          <v-btn color="white" variant="text" class="text-capitalize" @click="modal.snackbar.close()">
            {{ t(`com.close`) }}
          </v-btn>
        </template>
      </v-snackbar>
    </ClientOnly>
  </div>
</template>
<style lang="scss" scoped>
.bg-dark {
  background-color: rgb(40, 40, 40);
  color: rgb(0, 3, 27);
}
.fixed-group {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  font-size: 16px;
  // padding: 20px 0;
  @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
    padding: 50px 12px 50px 40px;
  }
  @media screen and (min-width: #{ 600 }px) {
    padding: 20px 0;
  }

  > .card {
    @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
      width: 100%;
    }

    @media screen and (min-width: #{ 600 }px) {
      width: 70%;
    }
    background-color: rgb(255, 255, 255);
    > .card-body {
      @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
        padding: 10px;
      }
      @media screen and (min-width: #{ 600 }px) {
        padding: 20px;
      }
    }
  }
}
.breadcrumbs {
  background-color: rgb(245, 245, 245);
  margin: 10px;
  @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
    width: 100%;
  }

  @media screen and (min-width: #{ 600 }px) {
    width: 70%;
  }
  .breadcrumbs-list {
    @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
      flex-direction: column;
      align-items: flex-start;
      .pc-divider {
        display: none;
      }
    }

    @media screen and (min-width: #{ 600 }px) {
      flex-direction: row;
      .sp-divider {
        display: none;
      }
    }
  }
}

.note {
  border: solid 2px gray;
  border-radius: 4px;
  background-color: rgb(239, 243, 255);

  .note-title {
    line-height: 2em;
    border-bottom: solid 1px gray;
    padding: 2px 10px;
    font-size: 18px;
    @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
      font-size: 16px;
    }

    @media screen and (min-width: #{ 600 }px) {
      font-size: 18px;
    }
  }

  .note-body {
    @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
      font-size: 14px;
      padding: 2px 6px;
    }

    @media screen and (min-width: #{ 600 }px) {
      font-size: 16px;
      padding: 2px 10px;
    }

    .sub-title {
      padding: 0;
      margin: 0 0 10px 0;
    }

    .index {
      font-weight: bold;
      padding: 0;
      margin: 10px 0 0 0;
    }
    .list,
    .text {
      @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
        padding: 0 0 0 6px;
      }

      @media screen and (min-width: #{ 600 }px) {
        padding: 0 0 0 10px;
      }
    }
    .list {
      padding: 0 0 0 20px;
      @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
        padding: 0 0 0 6px;
      }

      @media screen and (min-width: #{ 600 }px) {
        padding: 0 0 0 20px;
      }

      list-style-type: none;
      .list-item {
        padding: 0 0 0 16px;
      }
      .list-item::before {
        content: '\f111';
        font-family: 'Font Awesome 6 Free';
        font-weight: 900;
        font-size: 10px;
        line-height: 1em;
        position: absolute;
        inset: 0 auto 0 0;
        width: 16px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
    .info {
      margin: 0 0 10px 20px;
      padding: 0 0 0 8px;
      &::before {
        content: '';
        position: absolute;
        inset: 0 auto 0 0px;
        width: 3px;
        background-color: gray;
      }
    }
  }
}
.note-index {
  font-size: 18px;
  line-height: 2em;
  font-weight: bold;
  padding: 10px;
}
.note-list {
  padding: 10px;
}
.modal-container {
  z-index: 2;
  position: fixed;
  inset: 0;
  background-color: rgba(23, 29, 78, 0.47);
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  opacity: 0;
  transition: opacity 300ms;

  &.isShow {
    pointer-events: all;
    opacity: 1;
  }

  > .card {
    max-height: 100%;
    max-width: 100%;
    width: 70%;

    > .card-body {
      overflow: auto;
    }
  }
}
</style>
